<script>
// Dashboard.vue
import { defineAsyncComponent, ref } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import AppNavbar from 'next/navbar/AppNavbar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import SlaMissedToaster from 'dashboard/components-next/sla/SlaMissedToaster.vue';
import { SLA_TOASTS_ON_SCREEN } from 'dashboard/components-next/sla/useSlaMissedQueue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWindowSize } from '@vueuse/core';

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';
// VisionLauncher (botão flutuante) oculto por enquanto — o Vision só abre pelo
// SidepanelSwitch da conversa. Container segue montado pra renderizar o painel.
import VisionContainer from 'dashboard/components/vision/VisionContainer.vue';
import ElevenLabsLauncher from 'dashboard/components-next/elevenlabs/ElevenLabsLauncher.vue';
import ElevenLabsContainer from 'dashboard/components-next/elevenlabs/ElevenLabsContainer.vue';
import FlowBuilderLauncher from 'dashboard/components-next/elevenlabs/FlowBuilderLauncher.vue';

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';

// Sonner toast — montado aqui (Dashboard é layout pai do router-view) pra
// sobreviver às navegações internas de /telephony. Se ficasse no
// TelephonyLayout.vue, cada página remontaria o Toaster e perderia os
// toasts em transição (ex: clica Criar no form → router.push pra lista
// destrói o Toaster antes do toast loading renderizar).
import { Toaster } from 'vue-sonner';
import 'vue-sonner/style.css';

export default {
  components: {
    NextSidebar,
    AppNavbar,
    CommandBar,
    WootKeyShortcutModal,
    SlaMissedToaster,
    AddAccountModal,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
    VisionContainer,
    ElevenLabsLauncher,
    ElevenLabsContainer,
    Toaster,
    FlowBuilderLauncher,
    MobileSidebarLauncher,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
      windowWidth,
      SLA_TOASTS_ON_SCREEN,
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
      ].includes(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
  },
  watch: {
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },

  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<template>
  <div class="flex flex-col flex-grow overflow-hidden text-n-slate-12">
    <AppNavbar
      @show-create-account-modal="openCreateAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
    />
    <div class="flex flex-1 min-h-0 overflow-hidden">
      <NextSidebar
        :is-mobile-sidebar-open="isMobileSidebarOpen"
        @close-mobile-sidebar="closeMobileSidebar"
      />

      <main
        id="dashboard-main"
        class="flex flex-1 h-full w-full min-h-0 px-0 overflow-hidden"
      >
        <UpgradePage
          v-show="showUpgradePage"
          ref="upgradePageRef"
          :bypass-upgrade-page="bypassUpgradePage"
        >
          <MobileSidebarLauncher
            :is-mobile-sidebar-open="isMobileSidebarOpen"
            @toggle="toggleMobileSidebar"
          />
        </UpgradePage>
        <template v-if="!showUpgradePage">
          <router-view />
          <CommandBar />
          <CopilotLauncher />
          <ElevenLabsLauncher />
          <FlowBuilderLauncher />
          <MobileSidebarLauncher
            :is-mobile-sidebar-open="isMobileSidebarOpen"
            @toggle="toggleMobileSidebar"
          />
          <CopilotContainer />
          <VisionContainer />
          <ElevenLabsContainer />
        </template>
        <AddAccountModal
          :show="showCreateAccountModal"
          @close-account-create-modal="closeCreateAccountModal"
        />
        <!-- Toaster Sonner — montado globalmente no Dashboard pra cobrir tanto
           /telephony quanto qualquer rota onde os dialers (montados no AppNavbar)
           podem disparar toasts de status SIP.
           Sonner é silencioso quando nenhum toast() é chamado — não interfere
           no snackbar legado (newToastMessage) usado pelo resto do dashboard.
           visible-toasts: cabem os toasts de SLA estourado (teto individual +
           agregado); com o default 3 o mais antigo ficava invisível. -->
        <Toaster
          position="bottom-right"
          rich-colors
          close-button
          expand
          :duration="4000"
          :visible-toasts="SLA_TOASTS_ON_SCREEN"
        />
        <WootKeyShortcutModal
          v-model:show="showShortcutModal"
          @close="closeKeyShortcutModal"
          @clickaway="closeKeyShortcutModal"
        />
        <!-- SLA estourado: ouve a notificação sla_missed_* do cable e abre um
           toast persistente (no Toaster acima) com som. Global porque a
           conversa pode estar fora da lista aberta (outra inbox/filtro). -->
        <SlaMissedToaster />
      </main>
    </div>
  </div>
</template>
